/**
 * Expedition Loadout: pack a 12-slot backpack, then play three event cards. Simple game rules of
 * our own (not Minecraft mechanics, not real-world survival advice), shown to players in the
 * "How it works" panel.
 *
 * Rules (ruleset v1):
 *  - Each adventure has a shelf of items. Every copy you pack costs its slots and gives its uses.
 *    The backpack holds 12 slots. Supplies are free every attempt and never kept.
 *  - You start with the adventure's energy (max 6). Food can be eaten at any time: it uses one
 *    food use and restores energy.
 *  - Each event card offers choices. A choice may use up item uses and change energy; it can be
 *    picked only if you have those uses and your energy would not drop below 0. Every event also
 *    has a safe choice that needs no items, and you can always head home (ends the trip, retry).
 *  - The trip is complete after the third event. Each choice scores points; gold when the score
 *    reaches the adventure's gold mark.
 */
import { ITEMS } from './expedition-adventures.ts';

export const EXPEDITION_RULESET = 1;
export const BACKPACK_SLOTS = 12;
export const MAX_ENERGY = 6;
export const MAX_COPIES = 4;
export const MAX_ACTIONS = 40;

export type ItemId =
  | 'torch'
  | 'blocks'
  | 'bread'
  | 'apple'
  | 'pickaxe'
  | 'ladder'
  | 'boat'
  | 'bottle'
  | 'shield'
  | 'cloak'
  | 'map'
  | 'compass'
  | 'shears'
  | 'shovel'
  | 'tent'
  | 'spyglass';

/** 8×8 pixel art; each character is a colour from PIXEL_COLORS, '.' is clear. */
export type PixelArt = string[];

export interface ExpItem {
  id: ItemId;
  name: string;
  /** Backpack slots one copy takes. */
  slots: number;
  /** Uses one copy gives. */
  uses: number;
  /** Food: energy restored per use. */
  restores?: number;
  /** One short line for the shelf. */
  text: string;
  icon: PixelArt;
}

export interface ExpChoice {
  id: string;
  /** Plain words, a primary-school reader can follow. */
  label: string;
  /** Item uses this choice spends. Empty for the safe choice. */
  use: Partial<Record<ItemId, number>>;
  /** Energy change (negative = tiring, positive = resting). */
  energy: number;
  points: number;
  /** What happens, shown after choosing and in the summary. */
  result: string;
}

export interface ExpEvent {
  id: string;
  title: string;
  text: string;
  scene: string;
  choices: ExpChoice[];
  /** Specific advice when the best choice was not used here. */
  tip: string;
}

export type Loadout = Partial<Record<ItemId, number>>;
export type Action = { eat: ItemId } | { choose: string };

export interface Adventure {
  id: string;
  version: number;
  title: string;
  objective: string;
  /** What to expect: enough to pack well. Nothing in the events is a surprise. */
  forecast: string[];
  shelf: ItemId[];
  startEnergy: number;
  events: [ExpEvent, ExpEvent, ExpEvent];
  /** Gold medal: score at least this. */
  gold: number;
  /** Known successful runs (tests prove them; never shown). */
  solutions: { loadout: Loadout; actions: Action[]; medal: 'gold' | 'solved' }[];
}

export interface ExpState {
  /** Index of the current event; 3 when the trip is over. */
  event: number;
  energy: number;
  uses: Partial<Record<ItemId, number>>;
  score: number;
  log: { event: string; choice: string }[];
  ended: null | 'complete' | 'home';
}

export type ExpError = { ok: false; error: string };

/** Always offered: ends the trip safely so it can be tried again. */
export const HOME: ExpChoice = {
  id: 'home',
  label: 'Head home safely',
  use: {},
  energy: 0,
  points: 0,
  result: 'You headed home safely.',
};

export const itemById = (id: string) => ITEMS.find((i) => i.id === id);

export const slotsUsed = (l: Loadout) =>
  Object.entries(l).reduce((n, [id, c]) => n + (itemById(id)?.slots ?? 0) * (c ?? 0), 0);

/** Checks the loadout and sets up the start of the trip. */
export function start(a: Adventure, loadout: Loadout): ExpState | ExpError {
  const uses: ExpState['uses'] = {};
  for (const [id, c] of Object.entries(loadout)) {
    const item = itemById(id);
    if (!item || !a.shelf.includes(item.id)) return { ok: false, error: 'item not on the shelf' };
    if (!Number.isInteger(c) || c! < 0 || c! > MAX_COPIES) return { ok: false, error: 'bad count' };
    if (c) uses[item.id] = c * item.uses;
  }
  if (slotsUsed(loadout) > BACKPACK_SLOTS) return { ok: false, error: 'backpack is too full' };
  return { event: 0, energy: a.startEnergy, uses, score: 0, log: [], ended: null };
}

export const choicesOf = (e: ExpEvent) => [...e.choices, HOME];

/** Why a choice cannot be picked right now, or null when it can. */
export function blocked(s: ExpState, c: ExpChoice): null | { missing: ItemId[]; tired: boolean } {
  const missing = (Object.entries(c.use) as [ItemId, number][])
    .filter(([id, n]) => (s.uses[id] ?? 0) < n)
    .map(([id]) => id);
  const tired = s.energy + c.energy < 0;
  return missing.length || tired ? { missing, tired } : null;
}

export function step(a: Adventure, s: ExpState, act: Action): ExpState | ExpError {
  if (s.ended) return { ok: false, error: 'the trip is over' };
  if ('eat' in act) {
    const item = itemById(act.eat);
    if (!item?.restores || !s.uses[item.id]) return { ok: false, error: 'nothing to eat' };
    return {
      ...s,
      energy: Math.min(MAX_ENERGY, s.energy + item.restores),
      uses: { ...s.uses, [item.id]: s.uses[item.id]! - 1 },
    };
  }
  const ev = a.events[s.event]!;
  const c = choicesOf(ev).find((x) => x.id === act.choose);
  if (!c) return { ok: false, error: 'unknown choice' };
  if (blocked(s, c)) return { ok: false, error: 'that choice is not possible now' };
  const uses = { ...s.uses };
  for (const [id, n] of Object.entries(c.use) as [ItemId, number][]) uses[id] = uses[id]! - n;
  const next = s.event + 1;
  return {
    event: c === HOME ? s.event : next,
    energy: Math.min(MAX_ENERGY, s.energy + c.energy),
    uses,
    score: s.score + c.points,
    log: [...s.log, { event: ev.id, choice: c.id }],
    ended: c === HOME ? 'home' : next === a.events.length ? 'complete' : null,
  };
}

export interface ExpResult {
  ok: true;
  state: ExpState;
  medal: 'gold' | 'solved' | null;
}

/** Replays a whole attempt. Pure and deterministic: the server uses this to award medals. */
export function replay(a: Adventure, loadout: Loadout, actions: Action[]): ExpResult | ExpError {
  if (actions.length > MAX_ACTIONS) return { ok: false, error: 'too many steps' };
  let s = start(a, loadout);
  for (const act of actions) {
    if ('ok' in s) return s;
    s = step(a, s, act);
  }
  if ('ok' in s) return s;
  if (!s.ended) return { ok: false, error: 'the trip is not finished' };
  return { ok: true, state: s, medal: expMedal(a, s) };
}

export const expMedal = (a: Adventure, s: ExpState) =>
  s.ended !== 'complete' ? null : s.score >= a.gold ? 'gold' : 'solved';

const best = (e: ExpEvent) => Math.max(...e.choices.map((c) => c.points));

/** One specific tip for the next attempt, from the first event that did not go best. */
export function tipFor(a: Adventure, s: ExpState): string {
  for (const ev of a.events) {
    const entry = s.log.find((l) => l.event === ev.id);
    const chosen = ev.choices.find((c) => c.id === entry?.choice);
    if (!chosen || chosen.points < best(ev)) return ev.tip;
  }
  return s.score >= a.gold
    ? 'Perfect trip! Can you do it with fewer slots in your backpack?'
    : 'Try a different plan for more points.';
}
